import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi, AdminSupplier } from '@dukene/shared';
import {
    Search,
    Plus,
    Users,
    CheckCircle,
    AlertCircle,
    Globe,
    Package,
    Store,
    MoreVertical,
    Filter,
} from 'lucide-react';
import { format } from 'date-fns';

export default function Suppliers() {
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('ALL');
    const [marketplaceFilter, setMarketplaceFilter] = useState<string>('ALL');
    const queryClient = useQueryClient();

    // Fetch suppliers
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-suppliers', search, typeFilter, marketplaceFilter],
        queryFn: () => adminApi.getSuppliers({
            search,
            integrationType: typeFilter !== 'ALL' ? typeFilter : undefined,
            isMarketplace: marketplaceFilter === 'YES' ? true : marketplaceFilter === 'NO' ? false : undefined,
        }),
    });

    // Approve supplier mutation
    const approveMutation = useMutation({
        mutationFn: (id: string) => adminApi.approveSupplier(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
        },
    });

    const suppliers = data?.suppliers || [];

    if (error) {
        return (
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-black text-black mb-2">Failed to Load Suppliers</h2>
                <p className="text-gray-500 text-sm">Please check your connection and try again.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-black tracking-tight text-black">Marketplace Suppliers</h1>
                    <p className="text-gray-500 mt-2">Add, verify, and manage global marketplace suppliers</p>
                </div>
                <Link to="/suppliers/new">
                    <button className="inline-flex items-center justify-center h-12 px-6 text-sm font-bold rounded-xl bg-black text-white hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl active:scale-95">
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Supplier
                    </button>
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                    { label: 'Total Suppliers', value: data?.pagination.total || 0, icon: Users, color: 'bg-black' },
                    { label: 'Marketplace', value: (suppliers as AdminSupplier[]).filter((s: AdminSupplier) => s.isMarketplace).length, icon: Globe, color: 'bg-indigo-600' },
                    { label: 'Verified', value: (suppliers as AdminSupplier[]).filter((s: AdminSupplier) => s.isVerified).length, icon: CheckCircle, color: 'bg-green-600' },
                    { label: 'Pending Verification', value: (suppliers as AdminSupplier[]).filter((s: AdminSupplier) => !s.isVerified).length, icon: AlertCircle, color: 'bg-orange-500' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-2 rounded-xl ${stat.color} text-white`}>
                                <stat.icon className="w-5 h-5" />
                            </div>
                        </div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
                        <p className="text-3xl font-black text-black mt-1">{stat.value}</p>
                    </div>
                ))}
            </div>

            {/* Filters & Search */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Filter Suppliers</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, phone or company..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-12 w-full rounded-xl border-2 border-gray-100 bg-gray-50/50 focus:border-black focus:bg-white focus:outline-none pl-10 pr-4 text-sm transition-all"
                        />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="h-12 rounded-xl border-2 border-gray-100 bg-gray-50/50 px-4 py-2 text-sm font-medium text-black focus:border-black focus:bg-white focus:outline-none transition-all cursor-pointer"
                    >
                        <option value="ALL">All Integration Types</option>
                        <option value="CATALOG">Catalog</option>
                        <option value="API_OAUTH">API (OAuth)</option>
                        <option value="API_ONLY">API Key</option>
                    </select>
                    <select
                        value={marketplaceFilter}
                        onChange={(e) => setMarketplaceFilter(e.target.value)}
                        className="h-12 rounded-xl border-2 border-gray-100 bg-gray-50/50 px-4 py-2 text-sm font-medium text-black focus:border-black focus:bg-white focus:outline-none transition-all cursor-pointer"
                    >
                        <option value="ALL">All Suppliers</option>
                        <option value="YES">Marketplace Only</option>
                        <option value="NO">Private Only</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50/50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Supplier</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stats</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Marketplace</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gray-200"></div>
                                                <div className="space-y-2">
                                                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                                                    <div className="h-3 w-32 bg-gray-100 rounded"></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td colSpan={5} className="px-6 py-4">
                                            <div className="h-6 w-full bg-gray-50 rounded-lg"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : suppliers.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-20 text-center">
                                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                        <h3 className="text-lg font-bold text-gray-900">No suppliers found</h3>
                                        <p className="text-gray-500">Try adjusting your search or filters</p>
                                    </td>
                                </tr>
                            ) : (
                                suppliers.map((supplier: AdminSupplier) => (
                                    <tr key={supplier.id} className="hover:bg-gray-50/80 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                {supplier.logo ? (
                                                    <img
                                                        src={supplier.logo}
                                                        alt={supplier.name}
                                                        className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center border border-gray-100">
                                                        <Store className="w-6 h-6 text-gray-400" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-black truncate">{supplier.name}</span>
                                                        {supplier.isVerified && (
                                                            <CheckCircle className="w-4 h-4 text-green-500 fill-green-50" />
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500 flex flex-col">
                                                        <span>{supplier.company || 'Private Supplier'}</span>
                                                        <span className="text-xs">{supplier.email}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                {supplier.isVerified ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                                        VERIFIED
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">
                                                        PENDING
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-gray-400 font-medium">
                                                    Joined {format(new Date(supplier.createdAt), 'MMM yyyy')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                                                    <Package className="w-3 h-3 text-gray-400" />
                                                    <span>{supplier.productCount} Products</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                                                    <Store className="w-3 h-3 text-gray-400" />
                                                    <span>{supplier.connectedStores} Stores</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <span className={`w-fit px-2 py-1 rounded-lg text-xs font-bold ${supplier.integrationType === 'API_OAUTH'
                                                    ? 'bg-indigo-100 text-indigo-700'
                                                    : supplier.integrationType === 'API_ONLY'
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {supplier.integrationType.replace('_', ' ')}
                                                </span>
                                                {supplier.integrationType === 'API_ONLY' && supplier.apiKey && (
                                                    <span className="text-[10px] text-gray-400 font-mono">
                                                        {supplier.apiKey.substring(0, 20)}...
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {supplier.isMarketplace ? (
                                                <div className="flex items-center gap-1.5 text-black">
                                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                                    <span className="text-xs font-black">ACTIVE</span>
                                                </div>
                                            ) : (
                                                <span className="text-xs font-medium text-gray-400">PRIVATE</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-end gap-2">
                                                {!supplier.isVerified && (
                                                    <button
                                                        onClick={() => approveMutation.mutate(supplier.id)}
                                                        disabled={approveMutation.isPending}
                                                        className="h-8 px-3 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700 transition-colors disabled:opacity-50"
                                                    >
                                                        Approve & Email
                                                    </button>
                                                )}
                                                <Link to={`/suppliers/${supplier.id}`}>
                                                    <button className="h-8 px-3 rounded-lg border-2 border-black text-black text-xs font-bold hover:bg-black hover:text-white transition-all">
                                                        Details
                                                    </button>
                                                </Link>
                                                <button className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-black hover:bg-gray-100 transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
